import React, { useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, RotateCw, Crop } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { readExifOrientation, orientationToRotation } from '../utils/exifOrientation';

/**
 * PhotoCropDialog — reusable crop + rotate step for photos that get
 * ADDED or REPLACED on an existing plant. Same visual + interaction
 * language as the initial identification flow (`add-plant-steps/CropStep.js`)
 * so users don't get two different UX for essentially the same task.
 *
 * Contract:
 *   - `open`: boolean — dialog visibility
 *   - `sourceFile`: File | null — the raw file the user picked
 *   - `onCancel()` — close without saving
 *   - `onDone(base64NoPrefix, dataUrl)` — the caller uploads the base64
 *     and can also use `dataUrl` for optimistic UI updates
 *   - `title`: optional custom heading (defaults to translated
 *     "Crop & rotate")
 *
 * The crop area is intentionally NOT initialised (undefined) so the
 * user can either draw a crop OR skip it entirely and just rotate.
 * When no crop is drawn we fall back to the full rotated image so
 * users who only care about orientation don't have to select the
 * whole photo.
 */
export const PhotoCropDialog = ({ open, sourceFile, onCancel, onDone, title }) => {
  const { t } = useLanguage();
  const [imagePreview, setImagePreview] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [working, setWorking] = useState(false);
  const imageRef = useRef(null);

  // Reset all state whenever a new file arrives so re-opening after a
  // prior crop doesn't leak stale rotation / crop selection. We also
  // pre-seed `rotation` from the file's EXIF Orientation tag so shots
  // that were already upright at capture render upright immediately —
  // no manual Rotate tap needed for the common phone-photo case.
  useEffect(() => {
    if (!open || !sourceFile) return;
    setCrop(undefined);
    setCompletedCrop(null);
    setRotation(0);
    let cancelled = false;
    (async () => {
      try {
        const orient = await readExifOrientation(sourceFile);
        if (!cancelled) setRotation(orientationToRotation(orient));
      } catch { /* leave rotation at 0 */ }
    })();
    const reader = new FileReader();
    reader.onload = () => { if (!cancelled) setImagePreview(reader.result); };
    reader.onerror = () => { if (!cancelled) setImagePreview(null); };
    reader.readAsDataURL(sourceFile);
    return () => { cancelled = true; };
  }, [open, sourceFile]);

  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  // Same canvas math as `AddPlantModal.getCroppedImage`. Returns a
  // JPEG data URL. If the user didn't draw a crop we still honour the
  // rotation by rendering the whole image through the rotated canvas.
  const renderCropped = () => {
    if (!imageRef.current) return imagePreview;
    const image = imageRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const angle = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(angle));
    const cos = Math.abs(Math.cos(angle));
    // If no crop drawn, use the full displayed image bounds.
    const effective = completedCrop && completedCrop.width && completedCrop.height
      ? completedCrop
      : { x: 0, y: 0, width: image.width, height: image.height };
    const cropWidth = effective.width * scaleX;
    const cropHeight = effective.height * scaleY;
    canvas.width = cropWidth * cos + cropHeight * sin;
    canvas.height = cropWidth * sin + cropHeight * cos;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);
    ctx.translate(-cropWidth / 2, -cropHeight / 2);
    ctx.drawImage(
      image,
      effective.x * scaleX, effective.y * scaleY, cropWidth, cropHeight,
      0, 0, cropWidth, cropHeight,
    );
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const handleDone = async () => {
    if (working) return;
    setWorking(true);
    try {
      const dataUrl = renderCropped();
      const base64 = (dataUrl && dataUrl.includes(','))
        ? dataUrl.split(',')[1]
        : null;
      if (!base64) throw new Error('empty crop output');
      onDone?.(base64, dataUrl);
    } catch (err) {
      console.error('PhotoCropDialog: render failed', err);
      onCancel?.();
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !working) onCancel?.(); }}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden"
        data-testid="photo-crop-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle style={{ color: 'hsl(143, 28%, 25%)' }}>
            {title || t('cropAndRotate') || 'Crop & rotate'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('cropAddPhotoBlurb') || 'Frame the plant and rotate if needed so it lands right-side up in your gallery.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4 space-y-3">
          <div className="relative rounded-xl overflow-hidden flex items-center justify-center" style={{ backgroundColor: 'hsl(82, 55%, 95%)', minHeight: 200 }}>
            {imagePreview ? (
              <ReactCrop crop={crop} onChange={(c) => setCrop(c)} onComplete={(c) => setCompletedCrop(c)}>
                <img
                  ref={imageRef}
                  src={imagePreview}
                  alt=""
                  style={{ transform: `rotate(${rotation}deg)`, maxHeight: '55vh' }}
                  className="max-w-full"
                  data-testid="photo-crop-image"
                />
              </ReactCrop>
            ) : (
              <div className="text-xs italic p-6" style={{ color: 'hsl(125, 12%, 45%)' }}>
                {t('loading') || 'Loading…'}
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ backgroundColor: 'hsl(143, 28%, 94%)', border: '1px solid hsl(143, 28%, 85%)' }}>
            <Crop className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(143, 28%, 35%)' }} />
            <p className="text-xs leading-relaxed" style={{ color: 'hsl(143, 28%, 25%)' }}>
              <strong>{t('cropInstructions') || 'Tip:'}</strong>{' '}
              {t('cropAddPhotoTip') || 'Drag to crop; tap Rotate to fix orientation. If you don\'t draw a crop, the full rotated photo is saved.'}
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => onCancel?.()}
              variant="outline"
              style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
              data-testid="photo-crop-cancel-button"
              disabled={working}
            >
              <X className="w-4 h-4 mr-1" />
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleRotate}
              variant="outline"
              data-testid="photo-crop-rotate-button"
              disabled={working || !imagePreview}
            >
              <RotateCw className="w-4 h-4 mr-1" />
              {t('rotate') || 'Rotate'}
            </Button>
            <Button
              onClick={handleDone}
              disabled={working || !imagePreview}
              className="flex-1"
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
              data-testid="photo-crop-done-button"
            >
              <Crop className="w-4 h-4 mr-2" />
              {t('doneCropping') || 'Done'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
